import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Person2OutlinedIcon from "@mui/icons-material/Person2Outlined";
import { Settings, BadgeCheck, Globe, Phone, Mail } from "lucide-react";
import Loader from "../../Loader/Loader";
import { showToast } from "../../../Helper/Toast";
import { useGlobalState } from "../../../Providers/GlobalStateProvider";
import { hitGetProfileApi } from "../../../Redux/Professional/GetProfileSlice";
import { hitGetStartVerificationVeriff } from "../../../Redux/Common/CreateVeriffVerificationSlice";
import External from "../../../assets/svg/External";
import profile_icon from "../../../assets/img/profileIcon.png";

const displayValue = (value) => {
  if (value == null) return "N/A";
  const text = String(value).trim();
  return text || "N/A";
};

const Profile = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setGlobalLoading } = useGlobalState();
  const [professionalData, setProfessionalData] = useState(null);
  const [loading, setLoading] = useState(true);

  const responseProfileProfessional = useSelector(
    (state) => state.getProfileProfessionalReducer.data,
  );

  useEffect(() => {
    if (!localStorage.getItem("professionalToken")) {
      navigate("/professional/login");
      return;
    }
    setGlobalLoading(true);
    dispatch(hitGetProfileApi());
  }, []);

  useEffect(() => {
    const handleWindowFocus = () => {
      if (localStorage.getItem("professionalToken")) {
        dispatch(hitGetProfileApi());
      }
    };

    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        localStorage.getItem("professionalToken")
      ) {
        dispatch(hitGetProfileApi());
      }
    };

    window.addEventListener("focus", handleWindowFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleWindowFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [dispatch]);

  useEffect(() => {
    if (responseProfileProfessional?.status === true) {
      setLoading(false);
      setGlobalLoading(false);
      setProfessionalData(responseProfileProfessional.data);
    } else {
      setGlobalLoading(false);
    }
  }, [responseProfileProfessional, setGlobalLoading]);

  const startIdVerification = async () => {
    const firstName = professionalData?.firstName?.trim();
    const lastName = professionalData?.lastName?.trim();
    if (!firstName || !lastName) {
      showToast(
        "Please complete your profile with first and last name before verifying",
        "error",
      );
      navigate("/professional/signup-one");
      return;
    }
    const response = await dispatch(
      hitGetStartVerificationVeriff({
        firstName,
        lastName,
        type: 1,
      }),
    );
    if (response.payload?.status === "success") {
      window.open(response.payload.verification.url, "_blank");
    } else {
      showToast("Failed to start verification. Please try again.", "error");
    }
  };

  const handleGetVerified = () => {
    navigate("/professional/get-verified");
  };

  if (loading) {
    setGlobalLoading(true);
    return (
      <div style={{ height: "100vh" }}>
        <Loader />
      </div>
    );
  }

  if (!professionalData) {
    return null;
  }

  const profileImage =
    professionalData.profileImage ||
    professionalData.companyLogo ||
    profile_icon;

  const displayName =
    [professionalData.firstName, professionalData.lastName]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    professionalData.companyName ||
    "Professional";

  const servicesLabel =
    professionalData.serviceIds?.length > 0
      ? professionalData.serviceIds.map((item) => item.name).join(", ")
      : "N/A";

  const cityLabel =
    professionalData.city ||
    professionalData.address?.split(",")[0]?.trim() ||
    "N/A";

  const phoneLabel = professionalData.mobileNumber
    ? `${professionalData.countryCode || ""} ${professionalData.mobileNumber}`.trim()
    : "N/A";

  const idVerificationStatus = Number(professionalData.idVerificationStatus ?? 0);
  const showIdVerification = idVerificationStatus !== 2;
  const isIdVerificationPending = idVerificationStatus === 1;
  const isIdVerificationRejected = idVerificationStatus === 3;
  const idVerificationTitle = isIdVerificationPending
    ? "Verification Submitted"
    : isIdVerificationRejected
      ? "Verification Failed"
      : "Verify Your Identity";
  const idVerificationDescription = isIdVerificationPending
    ? "Your verification documents have been submitted successfully and are pending admin review."
    : isIdVerificationRejected
      ? "Your last verification attempt was not approved. Please resubmit your documents to try again."
      : "Complete secure ID verification with our trusted partner.";
  const idVerificationButtonLabel = isIdVerificationPending
    ? "Pending Review"
    : isIdVerificationRejected
      ? "Retry Verification"
      : "Start Verification";

  return (
    <div className="pro-app-page pro-app-page--muted pro-profile-page">
      <div className="custom_container pro-profile-page__inner">
        <h1 className="pro-profile-page__title">My Profile</h1>

        {showIdVerification && (
          <section className="pro-profile-page__section">
            <h2 className="pro-profile-page__section-label">
              ID Verification <span className="pro-profile-page__required">*</span>
            </h2>
            <div className="pro-profile-id-card">
              <Person2OutlinedIcon className="pro-profile-id-card__icon" />
              <h3>{idVerificationTitle}</h3>
              <p>{idVerificationDescription}</p>
              <button
                type="button"
                className="pro-profile-btn pro-profile-btn--blue"
                onClick={startIdVerification}
                disabled={isIdVerificationPending}
              >
                {!isIdVerificationPending && (
                  <External style={{ width: 20, height: 20 }} />
                )}
                {idVerificationButtonLabel}
              </button>
            </div>
          </section>
        )}

        <div className="pro-profile-hero">
          <div className="pro-profile-hero__avatar-wrap">
            <img
              src={profileImage}
              alt={displayName}
              className="pro-profile-hero__avatar"
              onError={(e) => {
                e.currentTarget.src = profile_icon;
              }}
            />
          </div>
          <button
            type="button"
            className="pro-profile-hero__edit"
            onClick={() => navigate("/professional/signup-one")}
          >
            Edit Profile
          </button>
          <h2 className="pro-profile-hero__name">{displayName}</h2>
        </div>

        <div className="pro-profile-actions">
          <button
            type="button"
            className="pro-profile-actions__btn pro-profile-actions__btn--settings"
            onClick={() => navigate("/professional/settings")}
          >
            <Settings size={18} strokeWidth={2} aria-hidden />
            Settings
          </button>
          <button
            type="button"
            className="pro-profile-actions__btn pro-profile-actions__btn--verified"
            onClick={handleGetVerified}
          >
            <BadgeCheck size={18} strokeWidth={2} aria-hidden />
            Get Verified
          </button>
        </div>

        <section className="pro-profile-info-card">
          <h3 className="pro-profile-info-card__title">Basic Information</h3>
          <dl className="pro-profile-info-card__list">
            <div className="pro-profile-info-card__row">
              <dt>First Name</dt>
              <dd>{displayValue(professionalData.firstName)}</dd>
            </div>
            <div className="pro-profile-info-card__row">
              <dt>Last Name</dt>
              <dd>{displayValue(professionalData.lastName)}</dd>
            </div>
            <div className="pro-profile-info-card__row">
              <dt>City</dt>
              <dd>{displayValue(cityLabel === "N/A" ? null : cityLabel)}</dd>
            </div>
          </dl>
        </section>

        <section className="pro-profile-info-card">
          <h3 className="pro-profile-info-card__title">Professional Details</h3>
          <dl className="pro-profile-info-card__list">
            <div className="pro-profile-info-card__row">
              <dt>Services</dt>
              <dd>{servicesLabel}</dd>
            </div>
            <div className="pro-profile-info-card__row">
              <dt>Position</dt>
              <dd>{displayValue(professionalData.companyPosition)}</dd>
            </div>
            <div className="pro-profile-info-card__row">
              <dt>Years in Business</dt>
              <dd>
                {professionalData.yearsInBusiness != null &&
                professionalData.yearsInBusiness !== ""
                  ? professionalData.yearsInBusiness
                  : "N/A"}
              </dd>
            </div>
          </dl>
        </section>

        <section className="pro-profile-info-card">
          <h3 className="pro-profile-info-card__title">Contact Information</h3>
          <ul className="pro-profile-contact-list">
            <li>
              <span className="pro-profile-contact-list__icon" aria-hidden>
                <Globe size={18} strokeWidth={2} />
              </span>
              <span className="pro-profile-contact-list__value">
                {displayValue(professionalData.website)}
              </span>
            </li>
            <li>
              <span className="pro-profile-contact-list__icon" aria-hidden>
                <Phone size={18} strokeWidth={2} />
              </span>
              <span className="pro-profile-contact-list__value">
                {phoneLabel}
              </span>
            </li>
            <li>
              <span className="pro-profile-contact-list__icon" aria-hidden>
                <Mail size={18} strokeWidth={2} />
              </span>
              <span className="pro-profile-contact-list__value">
                {displayValue(professionalData.email)}
              </span>
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
};

export default Profile;
